import * as THREE from 'three';

/**
 * Every sign in the building, lettered into one canvas.
 *
 * One texture for all of them: each sign is a cell of the atlas, and each
 * plane that shows one carries that cell's corner coordinates. Text is set in
 * the system's own sans and serif — no font is fetched.
 *
 * The browser draws it. Asked for outside one (a unit test), it hands back a
 * blank texture and the same cell coordinates, so the geometry is identical.
 */

const CELL_W = 512;
const CELL_H = 128;
const COLS = 4;
const ROWS = 16;

export const STYLES = {
  // Back of house: the grey room plate by every door, white capitals.
  plate: { bg: '#3a3f47', fg: '#f2f2ee', font: '600 54px sans-serif', border: '#6a707a' },
  // Front of house: gold serif capitals on dark walnut.
  foh: { bg: '#2e1e14', fg: '#d8b46a', font: '600 56px serif', border: '#8a6a3a' },
  // Out in the lot: a parking-sign green.
  outdoor: { bg: '#1f5a3a', fg: '#f4f4ec', font: '700 52px sans-serif', border: '#f4f4ec' },
  // Keep out.
  warning: { bg: '#f2f0e8', fg: '#a8231c', font: '800 50px sans-serif', border: '#a8231c' },
  // Glowing red on black, over every door to the outside.
  exit: { bg: '#141414', fg: '#ff3b2e', font: '800 84px sans-serif', border: '#141414' },
  // The BEO board's header strip.
  header: { bg: '#1f3a2c', fg: '#f0e8d0', font: '700 44px sans-serif', border: '#c8b88a' },
};

export function createSignAtlas() {
  const hasCanvas = typeof document !== 'undefined';
  const canvas = hasCanvas ? document.createElement('canvas') : null;
  if (canvas) {
    canvas.width = CELL_W * COLS;
    canvas.height = CELL_H * ROWS;
  }
  const ctx = canvas?.getContext('2d') ?? null;
  let next = 0;
  const cells = new Map();

  function claim(key, paint) {
    if (cells.has(key)) return cells.get(key);
    if (next >= COLS * ROWS) return cells.get('__blank') ?? [0, 0, 0, 0];
    const col = next % COLS;
    const row = Math.floor(next / COLS);
    next += 1;
    const x = col * CELL_W;
    const y = row * CELL_H;
    if (ctx) {
      ctx.save();
      ctx.translate(x, y);
      paint(ctx, CELL_W, CELL_H);
      ctx.restore();
    }
    // Canvas y runs down; texture v runs up. flipY is on, so v = 1 - y.
    const uv = [x / canvas?.width || 0, 1 - (y + CELL_H) / (canvas?.height || 1), (x + CELL_W) / (canvas?.width || 1), 1 - y / (canvas?.height || 1)];
    cells.set(key, uv);
    return uv;
  }

  /** A lettered sign, fitted to the cell. */
  function sign(text, style = 'plate') {
    const s = STYLES[style];
    return claim(`${style}|${text}`, (c, w, h) => {
      c.fillStyle = s.border;
      c.fillRect(0, 0, w, h);
      c.fillStyle = s.bg;
      c.fillRect(6, 6, w - 12, h - 12);
      c.fillStyle = s.fg;
      c.font = s.font;
      c.textAlign = 'center';
      c.textBaseline = 'middle';
      const width = c.measureText(text).width;
      const squeeze = Math.min(1, (w - 40) / Math.max(width, 1));
      c.save();
      c.translate(w / 2, h / 2 + 3);
      c.scale(squeeze, 1);
      c.fillText(text, 0, 0);
      c.restore();
    });
  }

  /** Anything else: a cell painted by hand. */
  function custom(key, paint) {
    return claim(key, paint);
  }

  const texture = canvas ? new THREE.CanvasTexture(canvas) : new THREE.DataTexture(new Uint8Array([200, 200, 200, 255]), 1, 1);
  texture.colorSpace = THREE.SRGBColorSpace;
  texture.anisotropy = 4;

  return {
    sign,
    custom,
    texture,
    /** Call once every cell is lettered. */
    finish() {
      texture.needsUpdate = true;
    },
  };
}

/** Four BEOs side by side, as pinned up: white sheets, a coloured title bar, typed lines. */
export function paintSheet(seed) {
  return (c, w, h) => {
    const each = w / 4;
    for (let s = 0; s < 4; s += 1) {
      const x0 = s * each + 8;
      const sw = each - 16;
      c.fillStyle = '#f6f4ee';
      c.fillRect(x0, 3, sw, h - 6);
      c.fillStyle = ['#2a4a7a', '#7a2a2a', '#2a6a4a', '#6a5a2a'][(seed + s) % 4];
      c.fillRect(x0 + 6, 9, sw - 12, 10);
      c.fillStyle = '#707070';
      for (let line = 0; line < 9; line += 1) {
        const len = 0.45 + (((seed * 7 + s * 13 + line * 29) % 10) / 10) * 0.5;
        c.fillRect(x0 + 6, 27 + line * 10, (sw - 12) * len, 3);
      }
    }
  };
}

/** The time clock's face: a white dial, twelve marks. The hands are drawn as geometry. */
export function paintDial(c, w, h) {
  c.fillStyle = '#b9bdb8';
  c.fillRect(0, 0, w, h);
  const r = h / 2 - 8;
  for (const cx of [h / 2]) {
    c.fillStyle = '#f4f2ea';
    c.beginPath();
    c.arc(cx, h / 2, r, 0, Math.PI * 2);
    c.fill();
    c.strokeStyle = '#2a2a2a';
    c.lineWidth = 4;
    c.stroke();
    for (let i = 0; i < 12; i += 1) {
      const a = (i / 12) * Math.PI * 2;
      c.beginPath();
      c.moveTo(cx + Math.cos(a) * r * 0.78, h / 2 + Math.sin(a) * r * 0.78);
      c.lineTo(cx + Math.cos(a) * r * 0.92, h / 2 + Math.sin(a) * r * 0.92);
      c.stroke();
    }
  }
  // The card slot and the maker's plate, to the right of the dial.
  c.fillStyle = '#2a2a2a';
  c.fillRect(h + 20, h / 2 - 6, w - h - 60, 12);
  c.fillStyle = '#4a4a4a';
  c.font = '700 22px sans-serif';
  c.fillText('INSERT CARD', h + 20, h / 2 + 36);
}

/** A rack of time cards, one slot a person. */
export function paintCards(c, w, h) {
  c.fillStyle = '#8a8e92';
  c.fillRect(0, 0, w, h);
  for (let i = 0; i < 16; i += 1) {
    const x = 10 + i * ((w - 20) / 16);
    c.fillStyle = '#50565c';
    c.fillRect(x, 10, (w - 20) / 16 - 4, h - 20);
    c.fillStyle = i % 5 === 2 ? '#e8d890' : '#f2efe6';
    c.fillRect(x + 3, 4, (w - 20) / 16 - 10, h * 0.55);
  }
}

/** Locker doors: two tiers, louvres at the top of each, a handle. */
export function paintLockers(c, w, h) {
  const doors = 4;
  const dw = w / doors;
  for (let i = 0; i < doors; i += 1) {
    c.fillStyle = '#566470';
    c.fillRect(i * dw + 2, 2, dw - 4, h - 4);
    c.fillStyle = '#3c4650';
    for (let v = 0; v < 4; v += 1) c.fillRect(i * dw + dw * 0.25, 12 + v * 8, dw * 0.5, 3);
    c.fillStyle = '#b8bec4';
    c.fillRect(i * dw + dw * 0.75, h * 0.55, 6, 20);
  }
}
